import React, { useState, useEffect, useRef } from "react";

//  escolhe imagem pc2 conforme idioma (default pt)
const pickpc2 = (val, lang) => {
  if (typeof val === "string") return val;                 
  if (val && typeof val === "object") {
    return val[lang] || val.pt || Object.values(val)[0];   
  }
  return "";                                            
};

//dicionario de textos
const HERO_COPY = {
    pt: {
        heading: "Innovation That Matters",
        aboutLabel: "SOBRE",
        products: {
            collector: {
                title: "Invisible Collector",
                body: "Invisible Collector é um SaaS automático que recorre a Inteligência Artificial e algoritmos de Machine Learning para analisar portfólios de dívida e executar a mais eficaz estratégia de cobrança através dos melhores canais de notificação disponíveis em cada.",
                cta: "Ir para o Invisible Collector",
                ctaColor: "#E83B3C",
                wave: "/assets/onda.png",
                pc: "/assets/pc1.png",
                tabColor: "#E83B3C",
                base: "var(--hero-wave-collector, #FDF0DA)",
            },
            link: {
                title: "Invisible Link",
                body: "O Invisible.Link permite às empresas a criação de campanhas massivas de comunicações, por SMS, Voz etc. Permite a criação de subusers/subcontas, aluguer de bases de dados, instalações WhiteLabel e API HTTPS para integradores de TI. Totalmente em conformidade com o RGPD.",
                cta: "Ir para o Invisible Link",
                ctaColor: "#027B5C",
                wave: "/assets/onda2.png",
                pc: { pt: "/assets/pc2.png", en: "/assets/pc2_en.png" },
                tabColor: "#027B5C",
                base: "var(--hero-wave-link, #EFFAF8)",
            },
        },
    },
    en: {
        heading: "Innovation That Matters",
        aboutLabel: "ABOUT",
        products: {
            collector: {
                title: "Invisible Collector",
                body: "Invisible Collector is an automated SaaS that leverages Artificial Intelligence and Machine Learning algorithms to analyse debt portfolios and execute the most effective collection strategy through the best notification channels for each case.",
                cta: "Go to Invisible Collector",
                ctaColor: "#E83B3C",
                wave: "/assets/onda.png",
                pc: "/assets/pc1.png",
                tabColor: "#E83B3C",
                base: "var(--hero-wave-collector, #FDF0DA)",
            },
            link: {
                title: "Invisible Link",
                body: "Invisible.Link allows companies to build large-scale communication campaigns via SMS, Voice and more. It supports sub-accounts, database leasing, white-label deployments and an HTTPS API for IT integrators. Fully compliant with GDPR.",
                cta: "Go to Invisible Link",
                ctaColor: "#027B5C",
                wave: "/assets/onda2.png",
                pc: { pt: "/assets/pc2.png", en: "/assets/pc2_en.png" },
                tabColor: "#027B5C",
                base: "var(--hero-wave-link, #EFFAF8)",
            },
        },
    },
};

export default function Hero({ lang = "pt" }) {
    const [current, setCurrent] = useState("collector"); // "collector" | "link"
    const [isSwitching, setIsSwitching] = useState(false);
    const [hintTarget, setHintTarget] = useState(null);  // botão onde mostrar o cursor 5s antes

    const sectionRef = useRef(null);
    const linkBtnRef = useRef(null);
    const collectorBtnRef = useRef(null);
    const prevWaveRef = useRef(current);

    // timers do ciclo (25s hint, 30s switch)
    const hintTimerRef = useRef(null);
    const switchTimerRef = useRef(null);

 
    
    useEffect(() => {
        const handle = () => {
            if (!sectionRef.current) return;
            const rect = sectionRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight || document.documentElement.clientHeight;
            const sectionCenter = rect.top + rect.height / 2;
            const viewportCenter = windowHeight / 2;
            const maxDistance = windowHeight / 2 + rect.height / 2;
            let progress = (sectionCenter - viewportCenter) / maxDistance;
            progress = Math.max(-1, Math.min(1, progress));
            const offset = progress * 20;
            sectionRef.current.querySelectorAll(".wave")
                .forEach((el) => el.style.setProperty("--wave-offset", `${offset}px`));
        };
        handle();
        window.addEventListener("scroll", handle, { passive: true });
        window.addEventListener("resize", handle);
        return () => {
            window.removeEventListener("scroll", handle);
            window.removeEventListener("resize", handle);
        };
    }, []);

    // --- helpers do ciclo 30s + hint 25s ---
    const clearCycle = () => {
        if (hintTimerRef.current) { clearTimeout(hintTimerRef.current); hintTimerRef.current = null; }
        if (switchTimerRef.current) { clearTimeout(switchTimerRef.current); switchTimerRef.current = null; }
        setHintTarget(null);
    };

    const scheduleCycle = (from) => {
        clearCycle();
        const next = from === "collector" ? "link" : "collector";

        // 20s: mostrar cursor no botão de destino 
        hintTimerRef.current = setTimeout(() => {
            setHintTarget(next);
        }, 20000);

        // 25s: troca conteúdo e reinicia ciclo
        switchTimerRef.current = setTimeout(() => {
            triggerSwitchAnimation(next);
            setCurrent(next);
            setHintTarget(null);
            scheduleCycle(next);
        }, 25000);
    };

    // inicia o ciclo ao montar
    useEffect(() => {
        scheduleCycle(current);
        return () => clearCycle();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        prevWaveRef.current = current;
    }, [current]);

    // animação curta de transição
    const triggerSwitchAnimation = () => {
        setIsSwitching(true);
        setTimeout(() => setIsSwitching(false), 1400); // acompanha as novas keyframes fluidas
    };

    const copy = HERO_COPY[lang] || HERO_COPY.pt;
    const DATA = copy.products;
    const imgpc2 = pickpc2(DATA.link.pc, lang);

    const HintCursor = () => (
        <div className="pointer-events-none absolute top-5 right-1 rotate-[-20deg] transform-gpu flex items-center justify-center">
            <span className="absolute -inset-1.5 rounded-full animate-ping bg-black/20" />
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-hand-index-thumb" viewBox="0 0 16 16" aria-hidden="true">
                <path d="M6.75 1a.75.75 0 0 1 .75.75V8a.5.5 0 0 0 1 0V5.467l.086-.004c.317-.012.637-.008.816.027.134.027.294.096.448.182.077.042.15.147.15.314V8a.5.5 0 0 0 1 0V6.435l.106-.01c.316-.024.584-.01.708.04.118.046.3.207.486.43.081.096.15.19.2.259V8.5a.5.5 0 1 0 1 0v-1h.342a1 1 0 0 1 .995 1.1l-.271 2.715a2.5 2.5 0 0 1-.317.991l-1.395 2.442a.5.5 0 0 1-.434.252H6.118a.5.5 0 0 1-.447-.276l-1.232-2.465-2.512-4.185a.517.517 0 0 1 .809-.631l2.41 2.41A.5.5 0 0 0 6 9.5V1.75A.75.75 0 0 1 6.75 1M8.5 4.466V1.75a1.75 1.75 0 1 0-3.5 0v6.543L3.443 6.736A1.517 1.517 0 0 0 1.07 8.588l2.491 4.153 1.215 2.43A1.5 1.5 0 0 0 6.118 16h6.302a1.5 1.5 0 0 0 1.302-.756l1.395-2.441a3.5 3.5 0 0 0 .444-1.389l.271-2.715a2 2 0 0 0-1.99-2.199h-.581a5 5 0 0 0-.195-.248c-.191-.229-.51-.568-.88-.716-.364-.146-.846-.132-1.158-.108l-.132.012a1.26 1.26 0 0 0-.56-.642 2.6 2.6 0 0 0-.738-.288c-.31-.062-.739-.058-1.05-.046zm2.094 2.025" />
            </svg>
        </div>
    );

    // base comum (parallax + posicionamento)
    const waveBase = {
        transform: "translateY(var(--wave-offset, 0px))",
        willChange: "transform, opacity",
    };

    //  gera estilo de máscara com cor sólida exata
    const maskStyle = (imgUrl, hex) => ({
        ...waveBase,
        position: "absolute",
        inset: 0,
        height: "200%",
        top: "-1%",
        backgroundColor: hex,
        WebkitMaskImage: `url(${imgUrl})`,
        maskImage: `url(${imgUrl})`,
        WebkitMaskRepeat: "no-repeat",
        maskRepeat: "no-repeat",
        WebkitMaskSize: "cover",
        maskSize: "cover",
        WebkitMaskPosition: "bottom center",
        maskPosition: "bottom center",
    });



    // clique manual: troca e reinicia ciclo (mantendo lógica 25s/30s)
    const manualSwitch = (target) => {
        if (current === target) return;
        clearCycle();
        triggerSwitchAnimation(target);
        setCurrent(target);
        setHintTarget(null);
        scheduleCycle(target);
    };


    return (
        <section ref={sectionRef} className="relative overflow-hidden bg-white hero-section">
            {/* KEYFRAMES/HELPERS */}
            <style>{`
        @keyframes sheen-sweep {
          0%   { transform: translateX(-120%); opacity: 0.0; }
          10%  { opacity: 0.25; }
          50%  { opacity: 0.35; }
          100% { transform: translateX(120%); opacity: 0.0; }
        }
        @keyframes device-pop {
          0%   { transform: translateY(0) scale(1); filter: saturate(1); }
          40%  { transform: translateY(-4px) scale(1.01); filter: saturate(1.05); }
          100% { transform: translateY(0) scale(1); filter: saturate(1); }
        }
        .wave {
          position: relative;
          transform: translateY(var(--wave-offset, 0px));
          transition: opacity 420ms ease, filter 1200ms ease;
          transform-origin: center bottom;
          will-change: transform, opacity;
        }
        .wave::after {
          content: "";
          position: absolute;
          inset: -25% -12% -10% -12%;
          background: radial-gradient(95% 150% at 50% 15%, rgba(255,255,255,0.52) 0%, rgba(255,255,255,0.15) 45%, rgba(255,255,255,0) 70%);
          opacity: 0;
          pointer-events: none;
          mix-blend-mode: screen;
          transition: opacity 900ms ease, transform 1400ms cubic-bezier(.22,.61,.36,1);
          transform: translateX(-40%) skewX(-6deg);
        }
        .wave-enter::after {
          opacity: 0.85;
          transform: translateX(30%) skewX(-2deg);
        }
        .wave-exit::after {
          opacity: 0.45;
          transform: translateX(-55%) skewX(-10deg);
        }
        @keyframes wave-enter-motion {
          0%   { transform: translate3d(-14%, calc(var(--wave-offset, 0px) + 18px), 0) scaleY(1.08); }
          45%  { transform: translate3d(6%, calc(var(--wave-offset, 0px) - 10px), 0) scaleY(0.96); }
          75%  { transform: translate3d(-2%, calc(var(--wave-offset, 0px) + 4px), 0) scaleY(1.02); }
          100% { transform: translate3d(0, var(--wave-offset, 0px), 0) scaleY(1); }
        }
        @keyframes wave-exit-motion {
          0%   { transform: translate3d(0, var(--wave-offset, 0px), 0) scaleY(1); }
          40%  { transform: translate3d(5%, calc(var(--wave-offset, 0px) - 6px), 0) scaleY(1.04); }
          100% { transform: translate3d(-18%, calc(var(--wave-offset, 0px) + 24px), 0) scaleY(0.9); }
        }
        .wave-enter { animation: wave-enter-motion 1400ms cubic-bezier(.25,.8,.25,1) forwards; }
        .wave-exit  { animation: wave-exit-motion 1200ms cubic-bezier(.55,.09,.68,.53) forwards; }
        .device-wrap.animate img { animation: device-pop 600ms ease-out; }
        .sheen { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
        .sheen::before {
          content: "";
          position: absolute; top: 0; bottom: 0; width: 45%;
          background: linear-gradient(115deg,
            rgba(255,255,255,0.00) 0%,
            rgba(255,255,255,0.35) 40%,
            rgba(255,255,255,0.55) 55%,
            rgba(255,255,255,0.00) 100%);
          transform: translateX(-120%);
        }
        .sheen.play::before { animation: sheen-sweep 900ms cubic-bezier(.2,.6,.2,1) both; }
      `}</style>

            {/* WAVES */}
            <div className="pointer-events-none select-none absolute inset-0 w-full z-0">
                <div className={`wave-wrap ${isSwitching ? "animate" : ""}`}>
                    <div
                        aria-hidden="true"
                        className={`wave origin-bottom transition-opacity duration-700 ease-out ${current === "collector" ? "opacity-100" : "opacity-0"
                            }`}
                        style={maskStyle(DATA.collector.wave, DATA.collector.base)}
                    />
                    <div
                        aria-hidden="true"
                        className={`wave origin-bottom transition-opacity duration-700 ease-out ${current === "link" ? "opacity-100" : "opacity-0"
                            }`}
                        style={maskStyle(DATA.link.wave, DATA.link.base)}
                    />
                </div>

                {/* SCRIM topo */}
                <div
                    aria-hidden="true"
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(to top, rgba(255,255,255,0.00) 40%, rgba(255,255,255,0.15) 65%, rgba(255,255,255,0.35) 82%, rgba(255,255,255,0.55) 100%)",
                    }}
                />

                {/* SHEEN quando troca */}
                <div className={`sheen ${isSwitching ? "play" : ""}`} />
            </div>

            {/* CONTENT */}
            <div className="relative z-10 mx-auto max-w-[1200px] px-3 sm:px-6 md:px-8 lg:px-8 pt-20 sm:pt-32 md:pt-28 lg:pt-28 pb-20 sm:pb-48 md:pb-28 lg:pb-24">
                <h1 className="text-center font-heading font-bold text-4xl md:text-5xl text-ink hero-heading relative -translate-y-10 md:-translate-y-6 lg:-translate-y-8">
                    {copy.heading}
                </h1>
                {/* Tabs */}
                <div className="mt-2 sm:mt-4 md:mt-4 lg:mt-5 flex items-center justify-center gap-4 sm:gap-6 md:gap-8 lg:gap-10 text-xs sm:text-sm md:text-base mb-5 sm:mb-8 md:mb-10 lg:mb-12">          {/* Collector */}
                    <button
                        ref={collectorBtnRef}
                        type="button"
                        onClick={() => manualSwitch("collector")}
                        aria-pressed={current === "collector"}
                        className={[
                            "group relative transition-colors transform duration-200 ease-out rounded-full px-3 py-1.5",
                            "hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-200",
                            current === "collector" ? "text-[#E83B3C]" : "text-[#404040] hover:text-[#E83B3C]",
                        ].join(" ")}
                    >
                        Invisible <span className="font-bold">Collector</span>
                        <span
                            className={[
                                "block h-[2px] mt-1 rounded mx-auto origin-center transition-transform duration-300 transform w-1/2",
                                current === "collector" ? "scale-x-100 bg-[#E83B3C]" : "scale-x-0 bg-transparent",
                                "group-hover:scale-x-100 group-hover:bg-[#E83B3C]",
                            ].join(" ")}
                        />
                        {hintTarget === "collector" && <HintCursor />}
                    </button>

                    {/* Link */}
                    <button
                        ref={linkBtnRef}
                        type="button"
                        onClick={() => manualSwitch("link")}
                        aria-pressed={current === "link"}
                        className={[
                            "group relative transition-colors transform duration-200 ease-out rounded-full px-3 py-1.5",
                            "hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-slate-200",
                            current === "link" ? "text-[#027B5C]" : "text-[#404040] hover:text-[#027B5C]",
                        ].join(" ")}
                    >
                        Invisible <span className="font-bold">Link</span>
                        <span
                            className={[
                                "block h-[2px] mt-1 rounded mx-auto origin-center transition-transform duration-300 transform w-1/2",
                                current === "link" ? "scale-x-100 bg-[#027B5C]" : "scale-x-0 bg-transparent",
                                "group-hover:scale-x-100 group-hover:bg-[#027B5C]",
                            ].join(" ")}
                        />
                        {hintTarget === "link" && <HintCursor />}
                    </button>
                </div>

                {/* ROW */}
                <div className="grid grid-cols-1 md:grid-cols-2 items-center mt-2 md:mt-8 lg:mt-8 translate-y-[-4px] md:translate-y-[-6px] lg:translate-y-[-8px] gap-4 md:gap-10 lg:gap-12">
                    {/* IMAGEM / DEVICE */}
                    <div className={`relative order-1 md:order-2 device-wrap ${isSwitching ? "animate" : ""}`}>
                        <div className="relative w-full h-[200px] sm:h-[240px] md:h-[340px] lg:h-[380px]">
                            <div className="absolute inset-0 flex items-center justify-center">
                                <img
                                    src="/assets/pc1.png"
                                    alt="Invisible Collector"
                                    className={[
                                        "absolute inset-0 m-auto object-contain",
                                        "w-auto h-full",
                                        "max-w-[300px] sm:max-w-[420px] md:max-w-[620px] lg:max-w-[720px]",
                                        "transition-opacity duration-500 ease-out",
                                        current === "collector" ? "opacity-100" : "opacity-0 pointer-events-none",
                                    ].join(" ")}
                                />
                                <img
                                    src={imgpc2}
                                    alt="Invisible Link"
                                    className={[
                                        "absolute inset-0 m-auto object-contain",
                                        "w-auto h-full",
                                        "max-w-[300px] sm:max-w-[420px] md:max-w-[620px] lg:max-w-[720px]",
                                        "transition-opacity duration-500 ease-out",
                                        current === "link" ? "opacity-100" : "opacity-0 pointer-events-none",
                                    ].join(" ")}
                                />
                            </div>
                        </div>
                    </div>

                    {/* TEXT */}
                    <div className="relative order-2 md:order-1 -mt-2 sm:-mt-1 md:mt-0">
                        <div className="relative min-h-[220px] sm:min-h-[300px] md:min-h-[280px] lg:min-h-[240px]">
                            {/* Collector */}
                            <div
                                className={`absolute inset-0 transition-opacity duration-500 ease-out ${current === "collector" ? "opacity-100" : "opacity-0 pointer-events-none"
                                    }`}
                                aria-live="polite"
                            >
                                <p className="tracking-[.18em] text-[10px] sm:text-xs text-slate-500 hero-subtext">{copy.aboutLabel}</p>
                                <h2 className="mt-1 sm:mt-2 font-heading text-2xl sm:text-3xl md:text-[30px] lg:text-[32px] font-semibold">
                                    {DATA.collector.title}
                                </h2>
                                <p className="mt-1.5 sm:mt-3 md:mt-3.5 lg:mt-4 text-slate-700 leading-relaxed hero-subtext">
                                    {DATA.collector.body}
                                </p>
                                <a
                                    href="https://invisiblecollector.com"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group mt-3 sm:mt-4 md:mt-5 lg:mt-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-white text-sm font-semibold transform transition-transform transition-shadow duration-300 ease-out shadow-md hover:shadow-xl hover:-translate-y-1 hover:scale-[1.02] active:translate-y-0 filter hover:brightness-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white/40"
                                    style={{ backgroundColor: DATA.collector.ctaColor }}
                                >
                                    {DATA.collector.cta}
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="transition-transform duration-300 ease-out transform group-hover:translate-x-2">
                                        <path d="M5 12h14M13 5l7 7-7 7" />
                                    </svg>
                                </a>
                            </div>

                            {/* Link */}
                            <div
                                className={`absolute inset-0 transition-opacity duration-500 ease-out ${current === "link" ? "opacity-100" : "opacity-0 pointer-events-none"
                                    }`}
                                aria-live="polite"
                            >
                                <p className="tracking-[.18em] text-[10px] sm:text-xs text-slate-500 hero-subtext">{copy.aboutLabel}</p>
                                <h2 className="mt-1 sm:mt-2 font-heading text-2xl sm:text-3xl md:text-[30px] lg:text-[32px] font-semibold">
                                    {DATA.link.title}
                                </h2>
                                <p className="mt-1.5 sm:mt-3 md:mt-3.5 lg:mt-4 text-slate-700 leading-relaxed hero-subtext">
                                    {DATA.link.body}
                                </p>
                                <a
                                    href="https://link.invisiblecloud.pt"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="group mt-3 sm:mt-4 md:mt-5 lg:mt-6 inline-flex items-center gap-2 rounded-full px-4 py-2 text-white text-sm font-semibold transform transition-transform transition-shadow duration-300 ease-out shadow-md hover:shadow-xl hover:-translate-y-1 hover:scale-[1.02] active:translate-y-0 filter hover:brightness-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white/40"
                                    style={{ backgroundColor: DATA.link.ctaColor }}
                                >
                                    {DATA.link.cta}
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="transition-transform duration-300 ease-out transform group-hover:translate-x-2">
                                        <path d="M5 12h14M13 5l7 7-7 7" />
                                    </svg>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* base line */}
            <div className="absolute bottom-0 left-0 w-full h-px bg-slate-200/80 z-20" />
        </section>
    );
}
